/**
 * Editor → preview highlight bridge (EAA-1642).
 *
 * Clicking an object name in the editor's list has to light that object up in
 * the live three.js preview. Getting this wrong twice is worth recording,
 * because the topology is not what it looks like on screen.
 *
 * WHAT IT LOOKS LIKE: an editor panel and a preview panel, side by side in one
 * page, so a module-level listener set should join them.
 *
 * WHAT IT ACTUALLY IS: THREE separate JavaScript contexts.
 *   - `ContentDetailsEditor` renders `ACHEditor`  → an iframe (CSS isolation)
 *   - `ContentDetailsPreviewPanel` renders `ACHPlayer` → another iframe
 *   - the web-content page hosting both is a third
 * `Content3DEdit` and `Content3DPlayer` therefore run in SIBLING frames, each
 * with its own copy of this module. An in-process set reaches nobody, and even
 * a parent+children relay fails: the editor posts up to the host page, and the
 * host page — which never mounts a player — had nothing listening to pass it
 * on. The events were not lost; they were delivered to nobody.
 *
 * SO: every context that merely IMPORTS this module joins a relay mesh. The
 * listener is installed eagerly at import rather than on first subscribe,
 * because the host page is a necessary hop and never subscribes to anything.
 * Each context notifies its own subscribers and forwards the message onward to
 * its parent and its child frames, minus the one it came from. A message id
 * plus a seen-set stops it circulating.
 *
 * Requests are scoped by `assetId` so a page showing two models highlights in
 * the right one.
 */

export interface PreviewHighlightRequest {
    /** Which model this is aimed at; a player with a different asset ignores it. */
    assetId: string;
    /** Object path to highlight, or null to clear. */
    objectPath: string | null;
}

type Listener = (request: PreviewHighlightRequest) => void;

const CHANNEL = 'content-3d:preview-highlight';

interface ChannelMessage {
    source: typeof CHANNEL;
    /** Dedupe key — the mesh has cycles (parent ↔ child) by construction. */
    id: string;
    request: PreviewHighlightRequest;
}

const listeners = new Set<Listener>();

/**
 * Primary transport. Every same-origin context — the host page and both blob
 * iframes, which inherit their creator's origin — joins one named channel, so
 * a message reaches siblings directly with no frame-walking, no reliance on
 * `document.querySelectorAll('iframe')` finding the preview, and no origin
 * juggling. The postMessage mesh below stays as the fallback for a frame whose
 * origin really is opaque, where BroadcastChannel would be isolated.
 */
let channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
    if (channel) return channel;
    if (typeof BroadcastChannel === 'undefined') return null;
    try {
        channel = new BroadcastChannel(CHANNEL);
        channel.onmessage = (event: MessageEvent) => {
            const data = event.data as ChannelMessage;
            if (!isChannelMessage(data) || !remember(data.id)) return;
            notifyLocal(data.request);
        };
    } catch {
        channel = null;
    }
    return channel;
}

/** Bounded FIFO of message ids already handled in THIS context. */
const seenIds = new Set<string>();
const seenOrder: string[] = [];
const SEEN_LIMIT = 64;

function remember(id: string): boolean {
    if (seenIds.has(id)) return false;
    seenIds.add(id);
    seenOrder.push(id);
    if (seenOrder.length > SEEN_LIMIT) {
        const oldest = seenOrder.shift();
        if (oldest) seenIds.delete(oldest);
    }
    return true;
}

/**
 * The payload is only an object path whose sole effect is highlighting a mesh,
 * and the player filters it by assetId on top, so this is deliberately
 * permissive about opaque origins — being strict here is what breaks the one
 * case the transport exists for.
 */
function isOriginTrusted(origin: string): boolean {
    const own = window.location.origin;
    // A blob:/sandboxed frame is itself opaque; it cannot make a meaningful
    // comparison, and its own origin reads as 'null' while the parent posting
    // to it reports a real origin. Rejecting that would silently drop every
    // message arriving INTO the preview frame.
    if (own === 'null' || !own) return true;
    return origin === own || origin === 'null';
}

function isChannelMessage(data: unknown): data is ChannelMessage {
    return Boolean(data) && (data as ChannelMessage).source === CHANNEL;
}

function notifyLocal(request: PreviewHighlightRequest): void {
    listeners.forEach((listener) => listener(request));
}

/** Post onward to the parent and every child frame, skipping where it came from. */
function relay(message: ChannelMessage, from?: Window | null): void {
    if (typeof window === 'undefined') return;
    // '*' rather than a fixed origin: a blob:/sandboxed preview frame has an
    // opaque origin and a targeted post would be silently dropped. Receivers
    // validate the channel name, the origin, and the assetId instead.
    const TARGET = '*';

    try {
        const parent = window.parent;
        if (parent && parent !== window && parent !== from) {
            parent.postMessage(message, TARGET);
        }
    } catch {
        /* inaccessible parent */
    }

    if (typeof document !== 'undefined') {
        document.querySelectorAll('iframe').forEach((frame) => {
            try {
                const child = frame.contentWindow;
                if (!child || child === from) return;
                child.postMessage(message, TARGET);
            } catch {
                /* inaccessible frame */
            }
        });
    }
}

function handleMessage(event: MessageEvent): void {
    if (!isOriginTrusted(event.origin)) return;
    if (!isChannelMessage(event.data)) return;
    if (!remember(event.data.id)) return;

    notifyLocal(event.data.request);
    relay(event.data, event.source as Window | null);
}

// Eager, at import: the host page is a required hop in the mesh and never
// calls subscribe, so a lazy install would leave the relay with a hole in the
// middle — which is exactly how the first version failed.
if (typeof window !== 'undefined') {
    window.addEventListener('message', handleMessage);
    getChannel();
}

/** Player side: listen for highlight requests. Returns an unsubscribe. */
export function subscribePreviewHighlight(listener: Listener): () => void {
    getChannel();
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Editor side: ask the matching preview to highlight an object (or clear). */
export function requestPreviewHighlight(request: PreviewHighlightRequest): void {
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    remember(id);
    notifyLocal(request);

    const message: ChannelMessage = { source: CHANNEL, id, request };
    try {
        getChannel()?.postMessage(message);
    } catch {
        /* channel unavailable — the mesh below still runs */
    }
    relay(message);
}
